import React, { useState, useEffect } from "react";
import BlackLogo from "../assets/ecositeAsset.png";
import CustomModal from "./CustomModal";
import BannerImage from "../assets/banner_1.webp";
import AndyAvatar from "../assets/andy_ecosite.png";
import TimAvatar from "../assets/tim_ecosite.png";
import { FaTwitter, FaLinkedin } from "react-icons/fa";

const useParallax = (speed = 0.5) => {
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setOffset(window.pageYOffset);
    };
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return -(offset * speed);
};

const About = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const parallaxOffset = useParallax(0.5);
  const settings = {
    dots: true,
    infinite: true,
    speed: 700,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
  };

  return (
    <div className="bg-green-50 min-h-screen">
      <header className="bg-[#2d9376] shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <img src={BlackLogo} alt="Logo" className="h-8 w-26" />
          <nav>
            <ul className="flex space-x-6">
              <li>
                <a
                  href="/"
                  className="text-black hover:bg-[#2d9376] transition duration-300"
                >
                  Home
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-black hover:text-green-400 transition duration-300"
                >
                  About
                </a>
              </li>
              <li>
                <a
                  href="/login"
                  className="text-black hover:text-green-400 transition duration-300"
                >
                  Portal
                </a>
              </li>
              <li>
                <a
                  href="#"
                  className="text-black hover:text-green-400 font-bold transition duration-300"
                  onClick={openModal}
                >
                  Contact Us
                </a>
              </li>
            </ul>
          </nav>
        </div>
      </header>
      <CustomModal isOpen={isModalOpen} onRequestClose={closeModal} />
      <div className="relative">
        <section
          className="relative bg-cover bg-center h-96 overflow-hidden"
          style={{ backgroundImage: `url(${BannerImage})` }}
        >
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            style={{ transform: `translateY(${parallaxOffset}px)` }}
          ></div>
          <div className="container mx-auto px-4 h-full flex items-center relative z-10">
            <div
              className="text-white max-w-3xl"
              style={{ transform: `translateY(${-parallaxOffset * 0.3}px)` }}
            >
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mt-6 mb-6 leading-tight">
                About us
              </h1>
              <p className="text-xl md:text-2xl mb-10 leading-relaxed">
                We find, assess and enable new sites for commercial solar.
              </p>
            </div>
          </div>
        </section>
      </div>
      <div className="max-w-7xl mx-auto pt-8">
        <section className="mb-16">
          <h2 className="text-3xl font-semibold mb-8">What We Do</h2>
          <ul className="list-disc list-inside text-lg space-y-4">
            <li className="flex items-start">
              <span className="mr-2 text-green-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              Our objective is to assist the UK to meet its net-zero energy
              goals by accelerating the UK’s commercial solar sector.
            </li>
            <li className="flex items-start">
              <span className="mr-2 text-green-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              Our WattSearch software uses AI to identify and assess unrealised
              commercial solar sites.
            </li>
            <li className="flex items-start">
              <span className="mr-2 text-green-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              We quantify the cost and carbon savings available to site owners
              and make it as easy as possible to realise them.
            </li>
            <li className="flex items-start">
              <span className="mr-2 text-green-500">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </span>
              We connect consultants, installer/operators and financiers to
              these new sites.
            </li>
          </ul>
        </section>
        <section className="mb-16">
          <h2 className="text-3xl font-semibold mb-4">About Our Company</h2>
          <p className="text-lg">
            We use AI to accelerate the UK commercial solar industry. Our
            WattSearch software identifies and scopes new sites, as well as
            assessing upfront the major hurdles that a normal sales process
            takes months to work through. We work with end clients to optimise
            the cost/benefit of commercial solar; installer/operators to secure
            new sales; and financial funds to build portfolios.
          </p>
        </section>

        <section className="mb-16">
          <h2 className="text-3xl font-semibold mb-8">Who We Are</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-lg relative">
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                <img
                  src={TimAvatar}
                  alt="Tim Matthews"
                  className="w-32 h-32 rounded-full border-4 border-white shadow-md"
                />
              </div>
              <h3 className="text-2xl font-bold mt-16 mb-2">
                Tim Matthews (CEO)
              </h3>
              <p className="text-lg mb-2">
                Lives close to the beach in Bournemouth with his wife, three
                children and over-enthusiastic dog. He’s a keen environmentalist
                and endurance sportsman.
              </p>
              <p className="text-lg mb-2">
                He holds a PhD in business science and worked as a Chartered
                Accountant, before becoming a priest in the Church of England.
              </p>
              <p className="text-lg">
                He serves on various trustee and director boards of local and
                regional companies and charities.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-lg relative">
              <div className="absolute -top-16 left-1/2 transform -translate-x-1/2">
                <img
                  src={AndyAvatar}
                  alt="Andy Dale"
                  className="w-32 h-32 rounded-full border-4 border-white shadow-md"
                />
              </div>
              <h3 className="text-2xl font-bold mt-16 mb-2">Andy Dale (CTO)</h3>
              <p className="text-lg mb-2">
                Lives in Poole with his wife, two children, two dogs and an
                unknown number of tropical fish. He’s a keen off-road cyclist
                and beach barbecue enthusiast.
              </p>
              <p className="text-lg mb-2">
                He is an experienced IT consultant, transforming the digital
                delivery landscapes of numerous multinational companies.
              </p>
              <p className="text-lg">
                During the pandemic, he founded and led a digital inclusion
                non-profit, working with frontline charities, ensuring that
                everyone could stay connected and thrive in an increasingly
                digital world.
              </p>
            </div>
          </div>
        </section>
      </div>
      {/* Footer */}
      <footer className="bg-gray-800 text-white py-8">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-4">About</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/about" className="hover:text-green-400">
                    Our Story
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Follow Us</h3>
              <div className="flex space-x-4">
                <a
                  href="https://x.com/ecositeltd"
                  target="_blank"
                  className="text-xl hover:text-green-400"
                >
                  <FaTwitter />
                </a>
                <a
                  href="https://www.linkedin.com/company/ecosite-ltd/"
                  target="_blank"
                  className="text-xl hover:text-green-400"
                >
                  <FaLinkedin />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-gray-700 text-center">
            <p>&copy; 2024 Ecosite. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
